import React from 'react';
import PropTypes from 'prop-types';

import CheckCircleIcon from '../../../assets/images/pro/greenCheck-filled';

import HeadText from '../../components/shared/text/HeadText';
import BulletList from '../../components/shared/text/BulletList';
import PageSwiper from '../../components/shared/PageSwiper';
import Expandable from '../../components/shared/Expandable';

import PlanType from '../../components/forms/pro/PlanType';
import PaymentForm from '../../components/forms/pro/PaymentForm';
import LoginForm from '../../components/forms/LoginForm';
import SignUpForm from '../../components/forms/SignUpForm';

import { ServerCommunicationUtil } from '../../utils/server_communication_util';
import { ProPageStrings } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';

import baseStyles from '../../styles/baseStyles.module.scss';
import styles from './styles/styles.module.scss';

// todo: resolve component composition issues that are resulting in repetition of text variables
// todo: add coupon validation and confirmation, add coupon amount to discount
// todo: need images/assets for continue with x and pay with x buttons.

class ProPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
      serverError: null,
      shownErrors: {},
      isAuthed: this.props.isAuthed,
      isFeaturesCollapsed: true,
      isRegistrationShown: true,
      isTypeSelected: this.props.initialPlan != null,
      activePlanType: this.props.initialPlan ? this.props.initialPlan : 0,
      activePaymentTab: 0,
      name: null,
      number: null,
      cvv: null,
      'cc-exp-month': null,
      'cc-exp-year': null,
      postalCode: null,
      verifiedCoupon: this.props.coupon,
      couponCode: this.props.coupon ? this.props.coupon.code : null,
      isCouponApplied: false
    };
  }

  componentDidMount() {
    if (this.state.verifiedCoupon) {
      this.setState({ isCouponApplied: true, isCouponInputExpanded: true, isCouponInputShown: true, submitting: false });
    }
  }

  clearErrors() {
    // Single function to clear shownErrors, server errors and coupon errors
    this.setState({
      shownErrors: {},
      couponError: '',
      serverError: null
    });
  }

  handleTypeClick = index => {
    this.setState({ activePlanType: index });
  };

  userFormSuccessCallback = data => {
    this.clearErrors();
    this.setState({ user: data.user, isAuthed: true, submitting: false });
  };

  handleRegistrationSuccess = data => {
    this.setState({ user: data.user, isAuthed: true });
  };

  handleRegisterClick = () => {
    this.clearErrors();
    this.setState({ isRegistrationShown: true });
  };

  handleLoginClick = () => {
    this.clearErrors();
    this.setState({ isRegistrationShown: false });
  };

  handleForgotPasswordClick = () => {
    window.location = '/users/password/new';
  };

  handleCouponCodeChange = event => {
    const { value } = event.target;
    this.setState({ couponCode: value, couponError: '' });
  };

  handleApplyCoupon = () => {
    this.handleCouponVerification(this.state.couponCode);
  };

  handleRemoveCoupon = () => {
    this.setState({
      isCouponInputExpanded: false,
      couponCode: null,
      isCouponApplied: false,
      verifiedCoupon: null
    });
  };

  handleTypeSubmit = () => {
    this.clearErrors();
    let analyticsMessage = 'plan_select_annual';
    if (this.state.activePlanType == 1) {
      analyticsMessage = 'plan_select_three_year';
    }
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_1', analyticsMessage]);
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_1', 'submit']);
    this.setState({ isTypeSelected: true });
  };

  clearSelectedType = () => {
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'change_plan']);
    this.setState({ isTypeSelected: false });
  };

  handleCouponVerification = couponCode => {
    // coupon_code is only allowed param in the API
    if (this.state.submitting) {
      // Not ideal, but this will prevent double submission (until loading state is added)
      return;
    }
    this.setState({ submitting: true });
    const data = {
      coupon_code: couponCode
    };
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      data,
      data => {
        this.setState({ verifiedCoupon: data[0], isCouponApplied: true, submitting: false });
        ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'coupon_code', data[0].code]);
      },
      err => {
        this.setState({ isCouponApplied: false, couponError: ProPageStrings.creditCardForm.couponCode.validation.generic, submitting: false });
      },
      () => {}
    );
  };

  handleShowCouponCodeInput = () => {
    this.setState({ isCouponInputExpanded: true });
  };

  getPlanTypesForm() {
    return (
      <PlanType
        text={ProPageStrings.planTypeText}
        handleSubmit={this.handleTypeSubmit}
        handleClick={this.handleTypeClick}
        activePlanType={this.state.activePlanType}
        planTypes={this.props.planTypes}
        currencyCode={this.props.currencyCode}
        verifiedCoupon={this.state.verifiedCoupon}
        isCouponApplied={this.state.isCouponApplied}
        serverError={this.state.serverError}
      />
    );
  }

  getLoginForm() {
    return (
      <LoginForm
        locale={this.props.locale}
        handleLoginSuccess={this.userFormSuccessCallback}
        handleForgotPasswordClick={this.handleForgotPasswordClick}
        handleRegisterClick={this.handleRegisterClick}
        enableRecaptcha={this.props.enableRecaptcha}
        facebookAppId={this.props.facebookAppId}
        googleClientId={this.props.googleClientId}
      />
    );
  }

  getRegistrationForm() {
    return (
      <SignUpForm
        isProPage
        locale={this.props.locale}
        userEmailSubscribed={this.props.inUnitedStates}
        showEmailSubscribed={!this.props.inUnitedStates}
        handleRegistrationSuccess={this.handleRegistrationSuccess}
        handleLoginClick={this.handleLoginClick} // questionable
        enableRecaptcha={this.props.enableRecaptcha}
        facebookAppId={this.props.facebookAppId}
        googleClientId={this.props.googleClientId}
        redirectTo={null}
      />
    );
  }

  getPaymentForm() {
    return (
      <PaymentForm
        locale={this.props.locale}
        recurlyPublicKey={this.props.recurlyPublicKey}
        activePlanType={this.state.activePlanType}
        planTypes={this.props.planTypes}
        handleApplycoupon={this.handleApplyCoupon}
        handleCouponCodeChange={this.handleCouponCodeChange}
        handleShowCouponCodeInput={this.handleShowCouponCodeInput}
        handleRemoveCoupon={this.handleRemoveCoupon}
        isCouponApplied={this.state.isCouponApplied}
        verifiedCoupon={this.state.verifiedCoupon}
        couponError={this.state.couponError} // Coupon Codes use a different error messaging system since they verify when applying
        isCouponInputExpanded={this.state.isCouponInputExpanded}
        isCouponInputShown={this.state.activePlanType === 0}
        selectedPlan={this.props.planTypes[this.state.activePlanType]}
        goBack={this.clearSelectedType}
        activePaymentTab={this.state.activePaymentTab}
        paymentTypes={this.props.paymentTypes}
        amazonClientId={this.props.amazonClientId}
        amazonSellerId={this.props.amazonSellerId}
        couponCode={this.state.couponCode}
        currencyCode={this.props.currencyCode}
      />
    );
  }

  getFeaturesContent() {
    const featuresList = <BulletList bullet={<img src={CheckCircleIcon} style={{ color: '#428a13' }} />} list={ProPageStrings.featuresList} />;
    return (
      <div className={`${styles.panel} ${styles.left}`}>
        <HeadText type="head3" text={ProPageStrings.headText} />

        <div className={`${styles.mobile} ${styles.collapsible}`}>
          <Expandable title={ProPageStrings.featuresCollapseHead}>{featuresList}</Expandable>
        </div>

        <div className={styles.desktop}>{featuresList}</div>
      </div>
    );
  }

  getLowerContent() {
    return (
      <div className={baseStyles.row}>
        <PageSwiper text={ProPageStrings.swiperText} />
        <div className={baseStyles.lower}>
          <div className={baseStyles.learnMore}>No Service. No Problem.</div>
        </div>
      </div>
    );
  }

  render() {
    // todo: this page switching logic has gotten way too complicated, this is more of an app than a page now.
    //        Should look into using React-Router instead.
    let formBody;
    let featuresContent;
    let lowerContent;
    if (this.state.isTypeSelected) {
      if (this.state.isAuthed) {
        formBody = this.getPaymentForm();
      } else if (this.state.isRegistrationShown) {
        formBody = this.getRegistrationForm();
      } else {
        formBody = this.getLoginForm();
      }
    } else {
      formBody = this.getPlanTypesForm();
      featuresContent = this.getFeaturesContent();
      lowerContent = this.getLowerContent();
    }
    return (
      <div id={styles.proPage}>
        <div className={baseStyles.container}>
          <div className={baseStyles.row}>
            <div className={`${styles.body} ${styles.topoBackground}`}>
              {featuresContent}
              <div className={`${styles.panel} ${!this.state.isTypeSelected ? styles.right : ''}`}>{formBody}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ProPage.propTypes = {
  isAuthed: PropTypes.bool,
  facebookAppId: PropTypes.string.isRequired,
  googleClientId: PropTypes.string.isRequired,
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      details: PropTypes.string,
      showDiscount: PropTypes.bool,
      discountAmount: PropTypes.string
    })
  ),
  paymentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      tab: PropTypes.number,
      showIcon: PropTypes.bool,
      iconPath: PropTypes.string
    })
  )
};

export default ProPage;
